import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation } from 'react-router-dom';
import { resolveCanonicalUrl } from 'lib/canonical';
import { resolveHreflangLinks } from 'lib/hreflang';

const OpenGraph = ({ fields }) => {
  const { title, type, url, image, description, alternates } =
    fields.value || {};
  const location = useLocation();

  // Sitecore reports the home item's URL as the bare root for both /us and /ca,
  // because the Layout Service only ever sees route `/` plus a language. The
  // locale lives in the path and nowhere the server can reach it, so the segment
  // is reapplied here. See lib/canonical.js.
  const canonicalUrl = resolveCanonicalUrl(url, location.pathname);

  // The market home pages (/, /us, /ca) each carry the same three alternates. Sitecore supplies
  // them, built from the config the sitemap reads, so the head and the sitemap cannot disagree -
  // and so that they do not depend on the request path, which the IIS locale rewrite can hide
  // from the renderer. Absent on every other page. See lib/hreflang.js.
  const hreflangLinks = resolveHreflangLinks(alternates);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <link rel='canonical' href={canonicalUrl} />
        <meta name='description' content={description} />
        <meta property='og:url' content={canonicalUrl} />
        <meta property='og:type' content={type} />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={description} />
        <meta property='og:image' content={image} />
      </Helmet>

      {/*
        Passed as the `link` prop rather than as JSX children, because Helmet serialises a
        child's prop names verbatim: <link hrefLang={...}> reaches the HTML as hrefLang="en-US",
        in React's casing. Browsers lowercase attribute names while parsing so that still works,
        but it stops the served markup matching the sitemap's hreflang="en-US" character for
        character, and that match is the point of sourcing both from one config. The prop API
        takes each attribute name from its object key, so it emits lowercase.

        Its own Helmet instance because children override props of the same type on a single
        instance - the canonical <link> above would otherwise drop these.
      */}
      <Helmet
        link={hreflangLinks.map(({ hreflang, href }) => ({
          rel: 'alternate',
          hreflang,
          href,
        }))}
      />
    </>
  );
};

export default OpenGraph;
